<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesTransactionPartyDocumentsAPI from 'dashboard/api/salesTransactionPartyDocuments';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  documents: { type: Array, default: () => [] },
  partyId: { type: [Number, String], required: true },
  transactionId: { type: [Number, String], required: true },
  summary: { type: Boolean, default: false },
  cardTitle: { type: String, default: '' },
});

const { t } = useI18n();
const documentTypeLabel = document =>
  document.document_types
    .map(type => type.name)
    .filter(Boolean)
    .join(', ');
const validityItems = document =>
  document.document_types.flatMap(type => {
    if (!type.validity_days) return [];

    return [
      {
        key: 'name',
        label: t('SALES_PARTIES.DOCUMENTS.DOCUMENT'),
        value: type.name,
      },
      {
        key: 'validity',
        label: t('SALES_PARTIES.DOCUMENTS.VALIDITY_DAYS'),
        value: type.validity_days,
      },
      {
        key: 'issued-on',
        label: t('SALES_PARTIES.DOCUMENTS.ISSUED_ON'),
        value: type.issued_on,
      },
      {
        key: 'expires-on',
        label: t('SALES_PARTIES.DOCUMENTS.EXPIRES_ON'),
        value: type.expires_on,
      },
    ];
  });
const documentsWithMetadata = computed(() =>
  props.documents.map(document => ({
    ...document,
    typeLabel: documentTypeLabel(document),
    validity: validityItems(document),
  }))
);

const download = async document => {
  try {
    const response = await SalesTransactionPartyDocumentsAPI.signedUrl(
      props.transactionId,
      props.partyId,
      document.id
    );
    window.open(response.data.data.url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('SALES_PARTIES.DOCUMENTS.DOWNLOAD_ERROR')
    );
  }
};
</script>

<template>
  <component
    :is="cardTitle ? LaisCard : 'div'"
    :title="cardTitle || undefined"
    class="grid gap-2"
  >
    <template v-if="summary">
      <div v-if="documentsWithMetadata.length" class="grid gap-4">
        <div
          v-for="document in documentsWithMetadata"
          :key="document.id"
          data-testid="sales-party-summary-document"
          class="grid gap-4 border-b border-lais-border-soft pb-4 last:border-b-0 last:pb-0 sm:grid-cols-2"
        >
          <div class="grid gap-1">
            <p class="text-xs font-semibold text-lais-text-muted">
              {{ t('SALES_PARTIES.DOCUMENTS.SUMMARY.TYPE') }}
            </p>
            <p class="text-sm text-lais-text-body">
              {{ document.typeLabel }}
            </p>
          </div>
          <div class="grid min-w-0 gap-1">
            <p class="text-xs font-semibold text-lais-text-muted">
              {{ t('SALES_PARTIES.DOCUMENTS.SUMMARY.NAME') }}
            </p>
            <button
              v-if="document.download_available"
              type="button"
              class="w-fit max-w-full truncate text-left text-sm font-semibold text-lais-primary hover:underline focus-visible:shadow-lais-focus"
              @click="download(document)"
            >
              {{ document.filename }}
            </button>
            <p v-else class="truncate text-sm text-lais-text-body">
              {{ document.filename }}
            </p>
          </div>
        </div>
      </div>
      <p v-else class="text-sm text-lais-text-muted">
        {{ t('SALES_PARTIES.DOCUMENTS.SUMMARY.EMPTY') }}
      </p>
    </template>
    <template v-else-if="documentsWithMetadata.length">
      <p v-if="!cardTitle" class="text-sm font-semibold text-lais-text">
        {{ t('SALES.DETAIL.PARTY_DOCUMENTS') }}
      </p>
      <div
        v-for="document in documentsWithMetadata"
        :key="document.id"
        class="grid gap-3 rounded-lais-sm border border-lais-border-soft p-3"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-lais-text-body">
              {{ document.filename }}
            </p>
            <p
              v-if="document.typeLabel"
              class="mt-1 text-xs text-lais-text-muted"
            >
              {{ document.typeLabel }}
            </p>
          </div>
          <LaisPrimaryButton
            v-if="document.download_available"
            size="sm"
            variant="ghost"
            icon="i-lucide-download"
            @click="download(document)"
          >
            {{ t('SALES.DETAIL.RECEIPT.DOWNLOAD') }}
          </LaisPrimaryButton>
        </div>
        <dl
          v-if="!summary && document.validity.length"
          class="grid gap-3 rounded-lais-sm bg-lais-bg-soft p-3 sm:grid-cols-2"
        >
          <template
            v-for="item in document.validity"
            :key="`${document.id}-${item.key}-${item.value}`"
          >
            <div>
              <dt
                class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
              >
                {{ item.label }}
              </dt>
              <dd class="mt-1 text-sm text-lais-text-body">
                {{ item.value || t('SALES.DETAIL.EMPTY_VALUE') }}
              </dd>
            </div>
          </template>
        </dl>
      </div>
    </template>
    <p v-else class="text-sm text-lais-text-muted">
      {{ t('SALES_PARTIES.COMMON.NO_DOCUMENT') }}
    </p>
  </component>
</template>
